import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useClearCheck, type CheckTransaction } from '@/hooks/queries/useChecks';
import { toast } from '@/components/ui/toast/useToast';
import { CheckCircle } from 'lucide-react';

interface ClearCheckModalProps {
  open: boolean;
  onClose: () => void;
  check: CheckTransaction | null;
}

export function ClearCheckModal({ open, onClose, check }: ClearCheckModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clearCheck = useClearCheck();

  const handleSubmit = async () => {
    if (!check) return;

    setIsSubmitting(true);

    try {
      await clearCheck.mutateAsync(check.id);
      toast.success(`Le chèque ${check.check_number} a été marqué comme encaissé.`);
      onClose();
    } catch (error) {
      console.error('Error clearing check:', error);
      toast.error('Impossible de marquer le chèque comme encaissé. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!check) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Marquer comme encaissé
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">N° Chèque:</span>
              <span className="font-mono font-semibold">{check.check_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Tireur:</span>
              <span className="font-medium">{check.check_drawer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Montant:</span>
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(check.amount)}
              </span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Traitement...' : '✅ Confirmer l\'encaissement'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
